import React, { useState, useEffect } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Collapse,
  IconButton,
  Typography,
  LinearProgress
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { getUsageStats, getUserTier, formatCredits, getUsagePercentage, getWarningLevel } from '../../services/usageService';

// Wyspur brand colors
const wyspurColors = {
  blue: '#0f71fb',    // Success/positive
  orange: '#ffaa01',  // Warning/70% threshold  
  pink: '#df3e99'     // Error/critical
};

/**
 * Calculate the next billing cycle reset date
 * If the current reset date has passed, show next month's reset date
 */
function getNextResetDate(resetDate) {
  if (!resetDate) return new Date();
  
  const currentDate = new Date();
  const currentResetDate = new Date(resetDate);
  
  // If the reset date for this month has already passed, show next month's reset date
  if (currentResetDate <= currentDate) {
    const nextResetDate = new Date(currentResetDate);
    nextResetDate.setMonth(nextResetDate.getMonth() + 1);
    return nextResetDate;
  }
  
  return currentResetDate;
}

/**
 * UsageAlertBanner - Shows warnings when credits are running low
 * 
 * This component displays:
 * - Low credit warnings (75%+ usage)
 * - Critical credit warnings (90%+ usage)
 * - Auto-refresh capability
 * - Dismissible alerts
 */
export default function UsageAlertBanner({ 
  autoRefresh = true,
  refreshInterval = 300000, // 5 minutes
  showOnlyWarnings = true 
}) {
  const [usageStats, setUsageStats] = useState(null);
  const [tierInfo, setTierInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(new Set());

  // Fetch usage data
  useEffect(() => {
    fetchUsageData();
    
    // Set up auto-refresh
    if (autoRefresh) {
      const interval = setInterval(fetchUsageData, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval]);

  const fetchUsageData = async () => {
    try {
      const [statsData, tierData] = await Promise.all([
        getUsageStats(),
        getUserTier()
      ]);
      
      if (statsData.success) {
        setUsageStats(statsData.data);
      }
      
      if (tierData.success) {
        setTierInfo(tierData.data);
      }
    } catch (err) {
      console.error('Error fetching usage data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Don't show anything while loading or if data is unavailable
  if (loading || !usageStats || !tierInfo) {
    return null;
  }

  // Use backend's authoritative credit values instead of calculating
  const creditsAvailable = usageStats?.creditsAvailable || 0;
  const creditsUsed = usageStats?.creditsUsed || 0;
  const creditsTotal = usageStats?.creditsTotal || tierInfo?.credits_per_month || 0;
  const usagePercentage = usageStats?.percentUsed || getUsagePercentage(creditsUsed, creditsTotal);
  const warningLevel = getWarningLevel(usagePercentage);
  const creditsRemaining = creditsAvailable; // Use actual database value, not calculated
  const isUnlimited = creditsTotal === -1;

  // Don't show banner for unlimited plans or if no warning needed
  if (isUnlimited || (showOnlyWarnings && warningLevel === 'none')) {
    return null;
  }

  // Generate unique key for dismissal tracking
  const alertKey = `${warningLevel}-${usagePercentage}`;
  
  // Don't show if dismissed
  if (dismissed.has(alertKey)) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(prev => new Set([...prev, alertKey]));
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchUsageData();
  };

  // Determine alert properties based on warning level with Wyspur colors
  const alertProps = {
    low: {
      severity: 'warning',
      icon: <WarningIcon />,
      title: 'Credits Running Low',
      color: 'warning',
      wyspurColor: wyspurColors.orange
    },
    critical: {
      severity: 'error',
      icon: <ErrorIcon />,
      title: 'Credits Almost Depleted',
      color: 'error',
      wyspurColor: wyspurColors.pink
    }
  };

  const currentAlert = alertProps[warningLevel];
  
  if (!currentAlert) {
    return null;
  }

  return (
    <Collapse in={true}>
      <Alert
        severity={currentAlert.severity}
        icon={currentAlert.icon}
        sx={{ 
          mb: 2,
          '& .MuiAlert-icon': {
            color: currentAlert.wyspurColor
          },
          borderColor: currentAlert.wyspurColor
        }}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              color="inherit"
              size="small"
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
            <IconButton
              color="inherit"
              size="small"
              onClick={handleDismiss}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
      >
        <AlertTitle>{currentAlert.title}</AlertTitle>
        
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2">
            You have <strong>{formatCredits(creditsRemaining)} credits remaining</strong> out of{' '}
            {formatCredits(creditsTotal)} ({usagePercentage}% used)
          </Typography>
          
          <Box sx={{ mt: 1, mb: 1 }}>
            <LinearProgress
              variant="determinate"
              value={usagePercentage}
              sx={{ 
                height: 6, 
                borderRadius: 3,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: currentAlert.wyspurColor
                }
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ color: currentAlert.wyspurColor, opacity: 0.9 }}>
            Contact the WyspurAI team for extra credits or plan upgrades.
          </Typography>
          
          <Typography variant="caption" color="text.secondary">
            Resets: {getNextResetDate(usageStats.resetDate || usageStats.month_reset_date).toLocaleDateString()}
          </Typography>
        </Box>

        {warningLevel === 'critical' && creditsRemaining < 10 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ color: wyspurColors.pink, fontWeight: 'medium' }}>
              ⚠️ Less than 10 credits remaining! New research jobs may be blocked.
            </Typography>
          </Box>
        )}
      </Alert>
    </Collapse>
  );
}